import React, { useState, useEffect } from 'react';
import { X, Palette } from 'lucide-react';
import { designerService } from '../../services/designerService';

/**
 * Compact multi-select for assigning designers.
 * Props mirror UserMultiSelect: selectedDesignerIds, onChange, disabled, label, placeholder, compact.
 */
const DesignerMultiSelect = ({
  selectedDesignerIds = [],
  onChange,
  disabled = false,
  label = "Designers",
  placeholder = "Select designers...",
  compact = false,
}) => {
  const [designers, setDesigners] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await designerService.getAll();
        const list = resp?.data || resp || [];
        if (mounted) setDesigners(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('DesignerMultiSelect fetch failed:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const idOf = (d) => d._id || d.id;

  const toggle = (id) => {
    if (disabled) return;
    const next = selectedDesignerIds.includes(id)
      ? selectedDesignerIds.filter(x => x !== id)
      : [...selectedDesignerIds, id];
    onChange(next);
  };

  const remove = (id, e) => {
    e?.stopPropagation();
    if (disabled) return;
    onChange(selectedDesignerIds.filter(x => x !== id));
  };

  const selected = designers.filter(d => selectedDesignerIds.includes(idOf(d)));

  return (
    <div className={compact ? "space-y-1" : "space-y-2"}>
      {label && <label className="block text-xs font-medium text-gray-600">{label}</label>}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {selected.map((d) => (
            <span
              key={idOf(d)}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs"
            >
              {d.name}
              {!disabled && (
                <button type="button" onClick={(e) => remove(idOf(d), e)} className="hover:bg-purple-200 rounded-full p-0.5">
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full px-2 py-1.5 border border-gray-300 rounded-md text-left flex items-center justify-between text-sm ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
          }`}
        >
          <span className="flex items-center gap-1.5 text-gray-600">
            <Palette className="w-3.5 h-3.5" />
            {selected.length > 0 ? `${selected.length} selected` : placeholder}
          </span>
          <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && !disabled && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
              ) : designers.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No designers configured</div>
              ) : (
                designers.map((d) => {
                  const id = idOf(d);
                  const isSelected = selectedDesignerIds.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggle(id)}
                      className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between text-sm ${isSelected ? 'bg-purple-50' : ''}`}
                    >
                      <span>{d.name}</span>
                      {isSelected && (
                        <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DesignerMultiSelect;
